// The map speed test behind the admin panel's "Map speed" tab (MapPerfTab.js): it opens kartvy and then listvy in a
// frame on this origin (`?perf=1`, src/perfRun.js: no analytics, and the view hands over its Leaflet map), with
// Stockholms län picked and no filters, and measures what the map does — loading, zooming, panning zoomed out and
// close, a fly and a pointer sweeping over the areas — as frame times from requestAnimationFrame and long tasks.
// The scenarios follow scripts/map-perf/bench.js (the Playwright benchmark), so the two read alike.
//
// The frame shares this origin's storage, so the state the views read (the picked areas, filters, Färger mode,
// travel time, visited homes, the comparison …) is set aside for the run and put back after it, also after a run
// cut short by a closed tab (restoreLeftoverState, called when the tab opens).
import buildInfo from '../buildInfo.json';

const CENTRE = [59.3326, 18.0649]; // Stockholm city
const FLY_TO = [59.1955, 17.6253]; // Södertälje
const ZOOM_STEPS = [11, 12, 13, 14, 15, 16, 17, 16, 15, 14, 13, 12, 11, 10, 9, 10];
const DRAGS = [[420, 0], [0, 260], [-420, 0], [0, -260], [300, 200], [-300, -200], [-300, 200], [300, -200]];
const DRAG_MS = 650;
const SETTLE_MS = 600;
const HOVER_MS = 4000;
const SLOW_FRAME_MS = 50;
// The frame's size: the largest of these that fits the window, so runs on one machine keep one size (it is part of
// the device a run is compared within) and listvy's side map isn't squeezed (275 px wide at 1280).
const FRAME_SIZES = [{ width: 1440, height: 810 }, { width: 1280, height: 720 }, { width: 1024, height: 640 }];
export const BAR_HEIGHT = 40;
export const pickFrameSize = () => FRAME_SIZES.find((size) => size.width <= window.innerWidth && size.height + BAR_HEIGHT <= window.innerHeight)
    || { width: window.innerWidth, height: window.innerHeight - BAR_HEIGHT };

export const VIEWS = [
    { id: 'kartvy', label: 'Kartvy', path: '/kartvy?perf=1', homes: '.kartvy-list-card' },
    { id: 'listvy', label: 'Listvy', path: '/?perf=1', homes: '.list-view-card-media' },
];

export const SCENARIOS = [
    { id: 'zoom', label: 'Zoom', hint: 'in step by step from 10 to 17 and out to 9' },
    { id: 'pan11', label: 'Pan, zoomed out', hint: 'eight drags at zoom 11 (squares and groups)' },
    { id: 'pan14', label: 'Pan, close', hint: 'eight drags at zoom 14 (house markers)' },
    { id: 'fly', label: 'Fly', hint: 'to Södertälje and back' },
    { id: 'hover', label: 'Hover', hint: 'the pointer sweeping over the areas at zoom 12' },
];

export const LOAD_METRICS = [
    { id: 'mapMs', label: 'Map up' },
    { id: 'areasMs', label: 'Areas coloured' },
    { id: 'homesMs', label: 'Homes shown' },
];

// ---------- the state the run sets aside ----------

const RESTORE_KEY = 'map_perf_restore_v1';
const RUN_COOKIES = {
    listing_filters: null,
    data_filter: null,
    map_toggles: null,
    data_state: null,
    area_filter_detailed: null,
    regso_hover_minimized: null,
    theme_mode: 'light',
    hide_onboarding: 'true',
};
const RUN_LOCAL = ['area_selection_v1', 'commute_v1', 'listing_visited_v1'];
const RUN_SESSION = ['view_state_v1', 'compare_areas_v1', 'fresh_bare_entry_v1', 'open_free_map_failed_v2'];

const readCookie = (name) => {
    const found = String(document.cookie || '').split(';').map((part) => part.trim())
        .find((part) => part.startsWith(`${name}=`));
    return found === undefined ? null : found.slice(name.length + 1);
};
const writeCookie = (name, value) => {
    document.cookie = value === null
        ? `${name}=; path=/; max-age=0`
        : `${name}=${value}; path=/; max-age=31536000`;
};
const storageGet = (storage, key) => {
    try {
        return window[storage].getItem(key);
    } catch (error) {
        return null;
    }
};
const storageSet = (storage, key, value) => {
    try {
        if (value === null) window[storage].removeItem(key);
        else window[storage].setItem(key, value);
    } catch (error) {
        // Storage blocked: nothing to set aside or put back.
    }
};

const setAside = () => {
    const saved = {
        cookies: Object.fromEntries(Object.keys(RUN_COOKIES).map((name) => [name, readCookie(name)])),
        local: Object.fromEntries(RUN_LOCAL.map((key) => [key, storageGet('localStorage', key)])),
        session: Object.fromEntries(RUN_SESSION.map((key) => [key, storageGet('sessionStorage', key)])),
    };
    storageSet('localStorage', RESTORE_KEY, JSON.stringify(saved));
    Object.entries(RUN_COOKIES).forEach(([name, value]) => writeCookie(name, value));
    RUN_LOCAL.forEach((key) => storageSet('localStorage', key, null));
    RUN_SESSION.forEach((key) => storageSet('sessionStorage', key, null));
};

// Puts back what a run set aside; also for a run a closed tab cut short.
export const restoreLeftoverState = () => {
    let saved = null;
    try {
        saved = JSON.parse(storageGet('localStorage', RESTORE_KEY) || 'null');
    } catch (error) {
        saved = null;
    }
    if (!saved) return false;
    Object.entries(saved.cookies || {}).forEach(([name, value]) => writeCookie(name, value));
    Object.entries(saved.local || {}).forEach(([key, value]) => storageSet('localStorage', key, value));
    Object.entries(saved.session || {}).forEach(([key, value]) => storageSet('sessionStorage', key, value));
    storageSet('localStorage', RESTORE_KEY, null);
    return true;
};

// Stockholms län as the pick both views open with (savedSelection.js's form; each view finds the län by name).
const seedPick = () => {
    storageSet('localStorage', 'area_selection_v1', JSON.stringify({
        v: 1,
        picks: [{ kind: 'lan', name: 'Stockholm', place: '', regsoIds: [] }],
        excluded: [],
        savedBy: 'perf',
        savedAt: Date.now(),
    }));
    RUN_SESSION.forEach((key) => storageSet('sessionStorage', key, null));
};

// ---------- the device ----------

const gpuName = () => {
    try {
        const gl = document.createElement('canvas').getContext('webgl');
        const ext = gl && gl.getExtension('WEBGL_debug_renderer_info');
        const name = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : '';
        return String(name || '').replace(/^ANGLE \((.*)\)$/, '$1').split(',').slice(0, 2).join(',').trim();
    } catch (error) {
        return '';
    }
};

const browserName = () => {
    const ua = navigator.userAgent;
    const edge = ua.match(/Edg\/(\d+)/);
    if (edge) return `Edge ${edge[1]}`;
    const chrome = ua.match(/Chrome\/(\d+)/);
    if (chrome) return `Chrome ${chrome[1]}`;
    const firefox = ua.match(/Firefox\/(\d+)/);
    if (firefox) return `Firefox ${firefox[1]}`;
    const safari = ua.match(/Version\/(\d+).*Safari/);
    return safari ? `Safari ${safari[1]}` : 'Browser';
};

const clean = (text) => String(text || '').replace(/[^\w .,:;()/|+\-@#]/g, '').trim();

// What a run's numbers depend on. Only runs with the same key are compared.
export const describeDevice = (frame, hz) => {
    const platform = clean(navigator.userAgentData?.platform || navigator.platform || 'unknown');
    const cores = navigator.hardwareConcurrency || 0;
    const dpr = Math.round((window.devicePixelRatio || 1) * 100) / 100;
    const env = process.env.NODE_ENV === 'production' ? 'production' : 'dev';
    const browser = browserName();
    const gpu = clean(gpuName());
    const size = `${frame.width}x${frame.height}`;
    return {
        key: [platform, `${cores} cores`, `DPR ${dpr}`, size, `${hz} Hz`, env, browser].join('|'),
        label: [platform, `${cores} cores`, gpu, `DPR ${dpr}`, `${frame.width}×${frame.height}`, `${hz} Hz`, browser].filter(Boolean).join(' · '),
        platform, cores, dpr, gpu, browser, hz, env, frame,
    };
};

// ---------- measuring ----------

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// `interval`: one refresh of the screen. A frame within 1.5 of them is smooth (on time), one up to 50 ms a small
// hitch (a frame or a few late), a longer one a stutter (`slow`).
const stats = (list, longTasks, interval) => {
    const sorted = [...list].sort((a, b) => a - b);
    const pick = (q) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))] || 0;
    const total = list.reduce((sum, value) => sum + value, 0);
    const round = (value) => Math.round(value * 100) / 100;
    return {
        frames: list.length,
        fps: total ? round(list.length / (total / 1000)) : 0,
        avg: list.length ? round(total / list.length) : 0,
        p50: round(pick(0.5)),
        p95: round(pick(0.95)),
        p99: round(pick(0.99)),
        max: round(sorted[sorted.length - 1] || 0),
        smooth: list.filter((value) => value <= interval * 1.5).length,
        hitch: list.filter((value) => value > interval * 1.5 && value <= SLOW_FRAME_MS).length,
        slow: list.filter((value) => value > SLOW_FRAME_MS).length,
        longTasks: longTasks.length,
        longTaskMs: Math.round(longTasks.reduce((sum, value) => sum + value, 0)),
    };
};

class Cancelled extends Error {}

export const isCancelled = (error) => error instanceof Cancelled;

// Drives one run. `frame` is the <iframe> the tab shows full size; `onProgress({ view, step, done, total })`;
// `signal` an AbortSignal (Cancel). Resolves with the run as it is stored.
export const runMapPerf = async ({ frame, onProgress, signal }) => {
    const total = VIEWS.length * (2 + SCENARIOS.length);
    const tally = { done: 0 }; // an object, so the per-view helpers below can count without a loop closure over a let
    const warnings = [];
    const check = () => {
        if (signal?.aborted) throw new Cancelled('Cancelled');
        if (document.visibilityState !== 'visible') {
            throw new Error('The tab was hidden during the test, which stops the map drawing. Keep it in front and run again.');
        }
    };
    const progress = (view, step) => onProgress?.({ view, step, done: tally.done, total });
    const waitFor = async (test, timeoutMs, what) => {
        const start = performance.now();
        for (;;) {
            check();
            const value = test();
            if (value) return value;
            if (performance.now() - start > timeoutMs) throw new Error(`Timed out waiting for ${what}`);
            await sleep(150);
        }
    };

    const startedAt = new Date();
    setAside();
    let hz = 60;
    const results = {};
    try {
        for (const view of VIEWS) {
            check();
            seedPick();
            progress(view.label, 'loading');
            const loaded = new Promise((resolve) => frame.addEventListener('load', resolve, { once: true }));
            frame.src = view.path;
            await loaded;
            const w = frame.contentWindow;
            const doc = frame.contentDocument;
            const { map } = await waitFor(() => w.__perfMap, 60000, 'the map');
            const mapMs = w.performance.now();
            const areasColoured = () => {
                const colours = new Set();
                map.eachLayer((layer) => {
                    if (layer.feature?.properties?.regsokod) colours.add(layer.options.color);
                });
                return colours.size >= 5;
            };
            await waitFor(areasColoured, 120000, 'the areas’ colours');
            const areasMs = w.performance.now();
            await waitFor(() => doc.querySelector(view.homes), 150000, 'the homes');
            const homesMs = w.performance.now();
            tally.done += 1;
            progress(view.label, 'waiting for the map to settle');

            // Quiet: no request in flight or finished for 2.5 s (the views keep loading listings for a while), then
            // still. The page counts its own requests (perfRun.js); a request finishing shows as a new resource entry.
            let lastCount = -1;
            let quietSince = performance.now();
            await waitFor(() => {
                const net = w.__perfNet;
                const count = w.performance.getEntriesByType('resource').length + (net ? net.inFlight : 0);
                if (count !== lastCount || (net && net.inFlight > 0)) {
                    lastCount = count;
                    quietSince = performance.now();
                }
                return performance.now() - quietSince > 2500;
            }, 120000, 'the requests to finish');
            await settle(map, 4000);

            const recorder = installRecorder(w);
            // The screen's refresh rate, from frames while nothing happens.
            const idle = await recorder.measure(() => sleep(1000));
            const idleMedian = [...idle.frames].sort((a, b) => a - b)[Math.floor(idle.frames.length / 2)] || 16.7;
            hz = Math.round(1000 / idleMedian / 5) * 5;
            const interval = 1000 / hz;
            tally.done += 1;

            const out = { load: { mapMs: Math.round(mapMs), areasMs: Math.round(areasMs), homesMs: Math.round(homesMs) } };
            const measure = async (id, setup, act) => {
                check();
                progress(view.label, SCENARIOS.find((scenario) => scenario.id === id).label.toLowerCase());
                await setup();
                check();
                const { frames, longTasks } = await recorder.measure(act);
                check();
                out[id] = stats(frames, longTasks, interval);
                tally.done += 1;
            };
            const at = async (centre, zoom) => {
                map.setView(centre, zoom, { animate: false });
                await settle(map, 2500);
            };

            await measure('zoom', () => at(CENTRE, 10), async () => {
                for (const zoom of ZOOM_STEPS) {
                    const ended = new Promise((resolve) => { map.once('zoomend', resolve); });
                    map.setZoomAround(map.getSize().divideBy(2), zoom);
                    await Promise.race([ended, sleep(3000)]);
                    await sleep(SETTLE_MS);
                }
            });
            // A drag that doesn't move the map (begun on a control over it) would measure a map standing still.
            const stillDrags = { count: 0 };
            const pan = async () => {
                stillDrags.count += DRAGS.length - await drags(w, map);
            };
            await measure('pan11', () => at(CENTRE, 11), pan);
            await measure('pan14', async () => {
                await at(CENTRE, 14);
                out.markers = doc.querySelectorAll('.leaflet-marker-icon').length;
            }, pan);
            if (stillDrags.count) {
                warnings.push(`${view.label}: ${stillDrags.count} of the ${DRAGS.length * 2} drags didn't move the map, so its pans aren't comparable.`);
            }
            await measure('fly', () => at(CENTRE, 10), async () => {
                for (const [target, zoom] of [[FLY_TO, 13], [CENTRE, 10]]) {
                    const ended = new Promise((resolve) => { map.once('moveend', resolve); });
                    map.flyTo(target, zoom, { duration: 1.6 });
                    await Promise.race([ended, sleep(5000)]);
                    await sleep(SETTLE_MS);
                }
            });
            await measure('hover', () => at(CENTRE, 12), () => sweep(w, map));

            // The base map falling back to raster tiles (OpenFreeMap judged failing) is a different workload.
            if (w.sessionStorage.getItem('open_free_map_failed_v2') || doc.querySelector('.leaflet-tile')) {
                out.fellBack = true;
                warnings.push(`${view.label}: the base map fell back to raster tiles, so this run isn't comparable.`);
            }
            recorder.stop();
            results[view.id] = out;
        }
    } finally {
        frame.src = 'about:blank';
        restoreLeftoverState();
    }

    const device = describeDevice({ width: frame.clientWidth, height: frame.clientHeight }, hz);
    return {
        v: 1,
        at: startedAt.toISOString(),
        durationS: Math.round((Date.now() - startedAt.getTime()) / 1000),
        env: device.env,
        build: {
            commit: buildInfo.hash || '',
            message: buildInfo.subject || '',
            committedAt: buildInfo.committedAt || '',
            builtAt: buildInfo.builtAt || '',
        },
        device,
        invalid: warnings.length > 0,
        warnings,
        results,
    };
};

// Frames and long tasks in the frame's own window, recorded while `act` runs.
const installRecorder = (w) => {
    const state = { on: false, frames: [], last: 0, stopped: false, longTasks: [] };
    const tick = (time) => {
        if (state.on) {
            if (state.last) state.frames.push(time - state.last);
            state.last = time;
        } else state.last = 0;
        if (!state.stopped) w.requestAnimationFrame(tick);
    };
    w.requestAnimationFrame(tick);
    let observer = null;
    try {
        observer = new w.PerformanceObserver((list) => {
            list.getEntries().forEach((entry) => state.longTasks.push({ start: entry.startTime, duration: entry.duration }));
        });
        observer.observe({ type: 'longtask' });
    } catch (error) {
        observer = null; // no Long Tasks API (Safari, Firefox): the counts stay 0
    }
    return {
        measure: async (act) => {
            state.frames = [];
            state.last = 0;
            const from = w.performance.now();
            state.on = true;
            await act();
            state.on = false;
            const to = w.performance.now();
            await sleep(120); // long tasks are reported just after they end
            const longTasks = state.longTasks.filter((task) => task.start >= from && task.start < to).map((task) => task.duration);
            return { frames: state.frames.slice(), longTasks };
        },
        stop: () => {
            state.stopped = true;
            observer?.disconnect();
        },
    };
};

// Until the map has stood still and nothing changed in its panes for quietMs.
const settle = (map, quietMs = 1500, maxMs = 60000) => new Promise((resolve) => {
    const start = performance.now();
    let last = performance.now();
    let moving = false;
    const observer = new MutationObserver(() => { last = performance.now(); });
    observer.observe(map.getPane('mapPane'), { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'class', 'd'] });
    const onStart = () => { moving = true; last = performance.now(); };
    const onEnd = () => { moving = false; last = performance.now(); };
    map.on('movestart zoomstart', onStart);
    map.on('moveend zoomend', onEnd);
    const tick = () => {
        const busy = moving || map._animatingZoom || (map._panAnim && map._panAnim._inProgress);
        if (busy) last = performance.now();
        if ((!busy && performance.now() - last > quietMs) || performance.now() - start > maxMs) {
            observer.disconnect();
            map.off('movestart zoomstart', onStart);
            map.off('moveend zoomend', onEnd);
            resolve();
        } else setTimeout(tick, 100);
    };
    tick();
});

const nextFrame = (w) => new Promise((resolve) => { w.requestAnimationFrame(resolve); });

const mouse = (w, type, x, y, buttons) => {
    const target = w.document.elementFromPoint(x, y) || w.document.body;
    target.dispatchEvent(new w.MouseEvent(type, {
        bubbles: true, cancelable: true, clientX: x, clientY: y, button: 0, buttons, view: w,
    }));
};

// A point on the bare map (not a control, the legend, a card or a marker over it), to begin a drag on.
const POINTS = [[0.62, 0.5], [0.5, 0.5], [0.4, 0.4], [0.6, 0.3], [0.3, 0.6], [0.7, 0.7], [0.5, 0.2], [0.2, 0.3]];
const bareMapPoint = (w, map) => {
    const container = map.getContainer();
    const rect = container.getBoundingClientRect();
    for (const [fx, fy] of POINTS) {
        const x = rect.left + rect.width * fx;
        const y = rect.top + rect.height * fy;
        const el = w.document.elementFromPoint(x, y);
        if (el && container.contains(el) && (el === container || el.closest('.leaflet-map-pane'))
            && !el.closest('.leaflet-marker-pane, .leaflet-popup-pane')) return [x, y];
    }
    return [rect.left + rect.width * 0.62, rect.top + rect.height * 0.5];
};

// Eight drags at a hand's pace, each let go (with its glide) and settled before the next. Resolves with how many
// of them moved the map.
const drags = async (w, map) => {
    const [x0, y0] = bareMapPoint(w, map);
    let moved = 0;
    for (const [dx, dy] of DRAGS) {
        const before = map.getCenter();
        mouse(w, 'mousedown', x0, y0, 1);
        const start = performance.now();
        let k = 0;
        let x = x0;
        let y = y0;
        while (k < 1) {
            await nextFrame(w);
            k = Math.min(1, (performance.now() - start) / DRAG_MS);
            const eased = k < 0.5 ? 2 * k * k : 1 - ((-2 * k + 2) ** 2) / 2;
            x = x0 + dx * eased;
            y = y0 + dy * eased;
            mouse(w, 'mousemove', x, y, 1);
        }
        const ended = new Promise((resolve) => { map.once('moveend', resolve); });
        mouse(w, 'mouseup', x, y, 0);
        await Promise.race([ended, sleep(3000)]);
        await sleep(SETTLE_MS);
        if (!map.getCenter().equals(before)) moved += 1;
    }
    return moved;
};

// The pointer sweeping over the areas (each lights up and shows its card), then leaving the map.
const sweep = async (w, map) => {
    const rect = map.getContainer().getBoundingClientRect();
    const start = performance.now();
    for (;;) {
        await nextFrame(w);
        const t = (performance.now() - start) / HOVER_MS;
        if (t >= 1) break;
        const x = rect.left + rect.width * (0.5 + 0.4 * Math.sin(t * Math.PI * 4));
        const y = rect.top + rect.height * (0.5 + 0.35 * Math.sin(t * Math.PI * 6 + 1));
        mouse(w, 'mousemove', x, y, 0);
    }
    map.getContainer().dispatchEvent(new w.MouseEvent('mouseout', { bubbles: true, relatedTarget: w.document.body, view: w }));
    await sleep(SETTLE_MS);
};
